import React, { memo, ReactNode } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { useGHL } from 'Context/GHL';

import classes from './ghlStatusGate.module.css';

interface Props {
  children: ReactNode;
  /**
   * Which SSO context the wrapped embed requires.
   * - 'location' (default): needs a resolved `locationId` + `ssoToken`
   *   (opportunity search, referral fees).
   * - 'company': company-scoped embeds need `companyId` + `ssoToken`
   *   (campaigns, contact lists).
   */
  requires?: 'location' | 'company';
}

/**
 * Renders the GHL SSO loading / error / "setup required" screens and only renders
 * its children once the required SSO context has resolved. Mirrors the states
 * `GHLMain` handles, so the standalone embed routes no longer render a blank
 * white screen on an unresolved or failed SSO handshake (WEBAPP-BUG-009).
 */
function GHLStatusGateComponent({ children, requires = 'location' }: Props) {
  const { locationId, ssoToken, companyId, error, errorType, isLoading } = useGHL();

  // Still waiting on the parent-iframe handshake / backend verification.
  if (isLoading) {
    return (
      <div className={classes.container}>
        <div className={classes.centeredCard}>
          <div className={classes.logoSection}>
            <div className={classes.logoIcon}>🚀</div>
            <h1 className={classes.logoText}>RocketPlan</h1>
          </div>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className={classes.statusText}>Connecting...</p>
        </div>
      </div>
    );
  }

  // Location exists but isn't linked to a RocketPlan company yet.
  if (errorType === 'LOCATION_NOT_LINKED') {
    return (
      <div className={classes.container}>
        <div className={classes.centeredCard}>
          <div className={classes.logoSection}>
            <div className={classes.logoIcon}>⚙️</div>
            <h1 className={classes.logoText}>Setup Required</h1>
          </div>
          <p className={classes.welcomeText}>This location needs to be configured</p>
          <p className={classes.instructionText}>
            Your GoHighLevel location has not been linked to a RocketPlan company yet.
            Please contact your administrator or RocketPlan support to complete the setup.
          </p>
          {locationId && (
            <div className={classes.locationInfo}>
              <p className={classes.locationLabel}>Location ID:</p>
              <code className={classes.locationCode}>{locationId}</code>
            </div>
          )}
          <div className={classes.supportInfo}>
            <p>Need help? Contact support with your location ID above.</p>
          </div>
        </div>
      </div>
    );
  }

  const hasContext = requires === 'company' ? Boolean(ssoToken && companyId) : Boolean(locationId && ssoToken);

  // Any other unresolved/failed state (timeout, missing token, generic error, or a
  // context that simply never arrived) — show the friendly "open from RocketCRM" screen
  // instead of a blank page.
  if (error || !hasContext) {
    return (
      <div className={classes.container}>
        <div className={classes.centeredCard}>
          <div className={classes.logoSection}>
            <div className={classes.logoIcon}>🚀</div>
            <h1 className={classes.logoText}>RocketPlan</h1>
          </div>
          <p className={classes.welcomeText}>Welcome to RocketPlan for RocketCRM</p>
          <p className={classes.instructionText}>
            To get started, please access this page from within your RocketCRM account
            where the RocketPlan app is installed.
          </p>
        </div>
      </div>
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}

const GHLStatusGate = memo(GHLStatusGateComponent, areEqual);

export { GHLStatusGate };
